import {
  Alert,
  Button,
  Checkbox,
  Code,
  Group,
  Select,
  Stack,
  Text,
  TextInput,
} from '@mantine/core';
import { useState } from 'react';
import { useBaselineApproval } from '@/hooks/api/automations/useExecutionCompositions.ts';
import { getHttpErrorMessage } from '@/services/httpRetry.ts';
import type { Baseline } from '@/types/executionCompositions.ts';
import type { BaselineApprovalStatus, ReleaseStatus } from '@/types/automationReleases.ts';
import { ExactDetails } from './CompositionPresentation.tsx';

type Decision = 'accepted' | 'disputed';
type Basis = 'internal_review' | 'customer_confirmation';

export default function BaselineApproval({
  releaseId,
  lifecycleRevision,
  baseline,
  approval,
  scopes,
  canEdit,
  status,
  onChanged,
}: {
  releaseId: string;
  lifecycleRevision: number;
  baseline: Baseline;
  approval?: BaselineApprovalStatus;
  scopes: string[];
  canEdit: boolean;
  status?: ReleaseStatus;
  onChanged: () => void;
}) {
  const mutation = useBaselineApproval();
  const [decision, setDecision] = useState<Decision | null>(null);
  const [basis, setBasis] = useState<Basis | null>(null);
  const [evidenceRef, setEvidenceRef] = useState('');
  const [acknowledged, setAcknowledledged] = useState(false);
  const [error, setError] = useState('');
  const isFrozen = status === 'frozen';
  const isActiveCandidate = status === 'draft' || isFrozen;
  const canSubmit =
    canEdit &&
    isActiveCandidate &&
    decision !== null &&
    basis !== null &&
    evidenceRef.trim().length > 0 &&
    acknowledged &&
    !mutation.isPending &&
    (!isFrozen || decision === 'disputed');

  const submit = async () => {
    if (!canSubmit || !decision || !basis) return;
    setError('');
    try {
      await mutation.mutateAsync({
        releaseId,
        request: {
          expected_lifecycle_revision: lifecycleRevision,
          baseline,
          status: decision,
          basis,
          evidence_ref: evidenceRef.trim(),
        },
      });
      setDecision(null);
      setBasis(null);
      setEvidenceRef('');
      setAcknowledledged(false);
      onChanged();
    } catch (cause) {
      setError(getHttpErrorMessage(cause));
      onChanged();
    }
  };

  return (
    <Stack gap="xs">
      <Text fw={600}>Baseline authority</Text>
      <Text size="sm">
        {approval?.status === 'accepted'
          ? 'An authorized reviewer accepted this exact baseline.'
          : approval?.status === 'disputed'
            ? 'An authorized reviewer disputed this baseline. It cannot qualify a matched or mismatch comparison.'
            : 'An authorized reviewer must review and decide whether this exact baseline is suitable.'}
      </Text>
      <ExactDetails
        summary="Exact baseline identity"
        values={{
          'Dataset ID': baseline.evidence_ref,
          'SHA-256': baseline.content_sha256,
          'Authority scope': baseline.authority_scope,
          'Used by': scopes.join(', '),
        }}
      />
      {approval && approval.status !== 'missing' && (
        <Text size="sm">
          Latest decision: <Code>{approval.status}</Code>
          {approval.basis && <> · {approval.basis.replaceAll('_', ' ')}</>}
          {approval.evidence_ref && (
            <>
              {' '}
              · evidence <Code>{approval.evidence_ref}</Code>
            </>
          )}
        </Text>
      )}
      {isFrozen && (
        <Alert color="orange">
          Freeze seals accepted baseline authority. Create a replacement Release before accepting
          this baseline. You may still record a dispute to withdraw its authority.
        </Alert>
      )}
      {canEdit && !isActiveCandidate && (
        <Alert color="gray">
          This Release is no longer active. Create a replacement Release to record a new baseline
          decision.
        </Alert>
      )}
      {canEdit && isActiveCandidate && (
        <>
          <Select
            label="Decision"
            placeholder="Select a decision"
            value={decision}
            onChange={(value) => setDecision(value as Decision | null)}
            data={[
              ...(isFrozen ? [] : [{ value: 'accepted', label: 'Accept baseline' }]),
              { value: 'disputed', label: 'Dispute baseline' },
            ]}
          />
          <Select
            label="Decision basis"
            placeholder="Select the basis"
            value={basis}
            onChange={(value) => setBasis(value as Basis | null)}
            data={[
              { value: 'internal_review', label: 'Internal review' },
              { value: 'customer_confirmation', label: 'Customer confirmation' },
            ]}
          />
          <TextInput
            label="Evidence reference"
            description="For example, a ticket, review note, or customer confirmation."
            value={evidenceRef}
            onChange={(event) => setEvidenceRef(event.currentTarget.value)}
            maxLength={128}
          />
          <Checkbox
            checked={acknowledged}
            onChange={(event) => setAcknowledledged(event.currentTarget.checked)}
            label="I reviewed this exact baseline and its intended comparison scope."
          />
          <Group>
            <Button
              disabled={!canSubmit}
              loading={mutation.isPending}
              onClick={() => void submit()}
            >
              Record baseline decision
            </Button>
          </Group>
        </>
      )}
      {error && <Alert color="red">{error}</Alert>}
    </Stack>
  );
}
